
import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { X, Clock, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

interface Announcement {
  id: string;
  emoji: string | null;
  message: string;
  link_url: string | null;
  link_text: string | null;
  expires_at: string | null;
  is_active: boolean;
}

const DISMISS_KEY_PREFIX = "announcement_dismissed_";

const AnnouncementBanner = () => {
  const { user } = useAuth();
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [countdown, setCountdown] = useState("");
  const [dismissed, setDismissed] = useState(false);

  const fetchActiveAnnouncement = useCallback(async () => {
    const query = supabase
      .from("announcements")
      .select("*")
      .eq("is_active", true)
      .order("created_at", { ascending: false })
      .limit(1);

    const { data, error } = await query;

    if (!error && data && data.length > 0) {
      const a = data[0];
      // Skip expired announcements (if they have an expiry)
      if (a.expires_at && new Date(a.expires_at) < new Date()) {
        setAnnouncement(null);
        return;
      }
      // Check if dismissed in localStorage (5 days)
      const dismissKey = `${DISMISS_KEY_PREFIX}${a.id}`;
      const dismissedAt = localStorage.getItem(dismissKey);
      if (dismissedAt) {
        const dismissDate = new Date(dismissedAt);
        const fiveDaysLater = new Date(dismissDate.getTime() + 5 * 24 * 60 * 60 * 1000);
        if (new Date() < fiveDaysLater) {
          setDismissed(true);
          return;
        } else {
          localStorage.removeItem(dismissKey);
        }
      }
      setAnnouncement(a);
      setDismissed(false);
    } else {
      setAnnouncement(null);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchActiveAnnouncement();
    }
  }, [user, fetchActiveAnnouncement]);

  // Countdown timer (only if expires_at is set)
  useEffect(() => {
    if (!announcement || !announcement.expires_at) {
      setCountdown("");
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const expires = new Date(announcement.expires_at!).getTime();
      const diff = expires - now;

      if (diff <= 0) {
        setAnnouncement(null);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      let str = "";
      if (days > 0) str += `${days}d `;
      if (hours > 0 || days > 0) str += `${hours}h `;
      str += `${minutes}m ${seconds}s`;

      setCountdown(str);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [announcement]);

  const handleDismiss = () => {
    if (announcement) {
      localStorage.setItem(`${DISMISS_KEY_PREFIX}${announcement.id}`, new Date().toISOString());
      setDismissed(true);
    }
  };

  if (!announcement || dismissed || !user) return null;

  return (
    <div className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white shadow-md z-[9999] relative">
      <div className="max-w-screen-xl mx-auto px-4 py-2.5 flex items-center gap-3">
        {/* Emoji */}
        <span className="text-lg flex-shrink-0">{announcement.emoji || "📢"}</span>

        {/* Message */}
        <p className="text-sm flex-1 min-w-0">
          <span>{announcement.message}</span>
          {announcement.link_url && (
            <a
              href={announcement.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 inline-flex items-center gap-1 underline font-semibold hover:opacity-80 transition-opacity"
            >
              {announcement.link_text || "Saiba mais"}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </p>

        {/* Countdown - only show if there's an expiry */}
        {countdown && (
          <div className="hidden sm:flex items-center gap-1.5 text-xs bg-white/15 rounded-full px-3 py-1 font-mono flex-shrink-0">
            <Clock className="h-3 w-3" />
            <span>{countdown}</span>
          </div>
        )}

        {/* Close */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 hover:bg-white/20 rounded-full p-1 transition-colors"
          title="Fechar aviso por 5 dias"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
